import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BackupPanel from "@/components/app/BackupPanel";

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    weight_unit: user?.weight_unit || "lb",
    current_password: "",
    new_password: "",
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({ name: user?.name || "", email: user?.email || "", weight_unit: user?.weight_unit || "lb", current_password: "", new_password: "" });
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      if (form.name !== user.name) body.name = form.name;
      if (form.email.trim().toLowerCase() !== user.email) body.email = form.email.trim();
      if (form.weight_unit !== (user.weight_unit || "lb")) body.weight_unit = form.weight_unit;
      if (form.new_password) {
        body.new_password = form.new_password;
        body.current_password = form.current_password;
      }
      if (Object.keys(body).length === 0) {
        toast.info("Nothing to save");
        setBusy(false);
        return;
      }
      const { data } = await api.patch("/auth/me", body);
      setUser?.(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit profile</DialogTitle>
          <DialogDescription>Change your name, email or password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="profile-form">
          <div>
            <Label className="hs-label">Full name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11 rounded-xl"
              data-testid="profile-name"
            />
          </div>
          <div>
            <Label className="hs-label">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11 rounded-xl"
              data-testid="profile-email"
            />
          </div>
          <div>
            <Label className="hs-label">Weight unit</Label>
            <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Preferred weight unit">
              {[
                { value: "lb", label: "Pounds (lb)", sub: "US standard" },
                { value: "kg", label: "Kilograms (kg)", sub: "Metric" },
                { value: "level", label: "Level (L)", sub: "Bikes, stacks" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={form.weight_unit === opt.value}
                  onClick={() => setForm({ ...form, weight_unit: opt.value })}
                  className={`p-3 rounded-xl border text-left transition-colors ${
                    form.weight_unit === opt.value
                      ? "border-moss bg-sage/40 text-moss"
                      : "border-line hover:border-moss/40 hover:bg-muted2"
                  }`}
                  data-testid={`profile-unit-${opt.value}`}
                >
                  <div className="font-heading font-semibold">{opt.label}</div>
                  <div className="text-[11px] text-ink-3 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-3 mt-2">Switching between lb/kg converts existing weights automatically. Level is stored as-is (no conversion).</p>
          </div>
          <div className="pt-2 border-t border-line">
            <p className="text-xs text-ink-3 mb-2">Leave blank to keep your current password.</p>
            <Label className="hs-label">Current password</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-2 h-11 rounded-xl font-data"
              placeholder="Required to change password"
              data-testid="profile-current-password"
            />
            <Label className="hs-label mt-3 block">New password</Label>
            <Input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-2 h-11 rounded-xl font-data"
              placeholder="At least 8 characters"
              data-testid="profile-new-password"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="profile-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
        <div className="pt-4 border-t border-line">
          <BackupPanel />
        </div>
      </DialogContent>
    </Dialog>
  );
}
